// Borrador de la carga de unidades con IA: las filas que devuelve la IA se
// revisan y completan en una tabla editable, y el avance queda guardado en
// este navegador (localStorage, por edificio) hasta guardar o descartar.

export const TIPOS_DOCUMENTO = ["CI", "DNI", "Pasaporte", "RUT", "Otro"];
export const ORIENTACIONES = ["Frente", "Contrafrente", "Lateral", "Interno"];

/** Campos editables de cada fila: los mismos nombres que la carga por Excel (unidadParaImportar). */
export const CAMPOS_UNIDAD = ["number", "floor", "m2", "valorCatastral", "bedroom", "bathroom", "balcony", "garage", "infront"];
export const CAMPOS_OCUPANTES = [
  "propNombre", "propApellido", "propTipoDoc", "propCedula", "propCelular", "propEmail",
  "inqNombre", "inqApellido", "inqTipoDoc", "inqCedula", "inqCelular", "inqEmail",
];
export const CAMPOS_FILA = [...CAMPOS_UNIDAD, ...CAMPOS_OCUPANTES];

/** Campos físicos que se actualizan en una unidad que ya existe (número y piso la identifican). */
const FISICOS_ACTUALIZABLES = ["m2", "valorCatastral", "bedroom", "bathroom", "balcony", "garage", "infront"];
const NUMERICOS = new Set(["m2", "valorCatastral", "bedroom", "bathroom"]);
const ENTEROS = new Set(["bedroom", "bathroom"]);

const texto = (v) => (v == null ? "" : String(v).trim());
const sinTildes = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "");

let secuencia = 0;
export const nuevoIdFila = () => `f${Date.now().toString(36)}${(secuencia++).toString(36)}`;

export const filaVacia = () => ({ _id: nuevoIdFila(), ...Object.fromEntries(CAMPOS_FILA.map((k) => [k, ""])) });

const siNo = (v) => {
  const s = sinTildes(texto(v).toLowerCase());
  if (["si", "s", "yes", "y", "true", "1", "x"].includes(s)) return "si";
  if (["no", "n", "false", "0"].includes(s)) return "no";
  return "";
};

const MAPA_ORIENTACION = { frente: "Frente", contrafrente: "Contrafrente", lateral: "Lateral", interno: "Interno", interior: "Interno" };
const orientacion = (v) => MAPA_ORIENTACION[sinTildes(texto(v).toLowerCase()).replace(/[\s_-]+/g, "")] || "";

const MAPA_DOC = { ci: "CI", cedula: "CI", dni: "DNI", pasaporte: "Pasaporte", rut: "RUT", otro: "Otro" };
const tipoDoc = (v) => MAPA_DOC[sinTildes(texto(v).toLowerCase())] || "";

/**
 * Unidad que devuelve la IA → fila editable. Los selects quedan solo con
 * valores que acepta importarUnidades (si no, vacíos para completar). Se
 * conservan los campos extra (propietario/inquilino secundario).
 */
export function filaDesdeUnidad(u = {}) {
  const fila = { ...u, _id: nuevoIdFila() };
  CAMPOS_FILA.forEach((k) => {
    fila[k] = texto(u[k]);
  });
  fila.balcony = siNo(u.balcony);
  fila.garage = siNo(u.garage);
  fila.infront = orientacion(u.infront);
  fila.propTipoDoc = tipoDoc(u.propTipoDoc);
  fila.inqTipoDoc = tipoDoc(u.inqTipoDoc);
  return fila;
}

/** Misma clave número + piso que importarUnidades (functions/identity/residentes.js normClave). */
const normClave = (v) => {
  const s = texto(v).toLowerCase().replace(/\s+/g, " ");
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};
export const claveUnidad = (number, floor) => `${normClave(number)}|${normClave(floor)}`;

const tieneClave = (f) => texto(f.number) !== "" && texto(f.floor) !== "";

/** Número con coma o punto decimal; "150.000" son miles (como aNumero en importarUnidades). */
export function aNumero(v) {
  if (typeof v === "number") return v;
  let s = texto(v).replace(/\s+/g, "");
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, "");
  else s = s.replace(",", ".");
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
}

/** Campos de la fila con un valor que el servidor rechazaría (para marcarlos en rojo). */
export function camposInvalidos(fila) {
  const malos = new Set();
  NUMERICOS.forEach((k) => {
    const v = texto(fila[k]);
    if (v === "") return;
    const n = aNumero(v);
    if (!Number.isFinite(n) || (ENTEROS.has(k) && !Number.isInteger(n))) malos.add(k);
  });
  ["propEmail", "inqEmail"].forEach((k) => {
    const v = texto(fila[k]);
    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) malos.add(k);
  });
  return malos;
}

/**
 * Estado de cada fila (por _id): "incompleta" (sin número o piso), "repetida"
 * (otra fila con el mismo número y piso), "existe" (ya está en el edificio:
 * se actualizan sus datos físicos) o "nueva" (se crea con importarUnidades).
 */
export function estadosDeFilas(filas, unidadesExistentes = []) {
  const existentes = new Map(unidadesExistentes.map((u) => [claveUnidad(u.number, u.floor), u]));
  const cuenta = new Map();
  filas.forEach((f) => {
    if (!tieneClave(f)) return;
    const c = claveUnidad(f.number, f.floor);
    cuenta.set(c, (cuenta.get(c) || 0) + 1);
  });
  const out = new Map();
  filas.forEach((f) => {
    if (!tieneClave(f)) return out.set(f._id, { estado: "incompleta" });
    const c = claveUnidad(f.number, f.floor);
    if (cuenta.get(c) > 1) return out.set(f._id, { estado: "repetida" });
    const unidad = existentes.get(c);
    return out.set(f._id, unidad ? { estado: "existe", unidad } : { estado: "nueva" });
  });
  return out;
}

const comparable = (k, v) => {
  if (v == null || v === "") return "";
  if (k === "balcony" || k === "garage") return String(v === true || siNo(v) === "si");
  if (NUMERICOS.has(k)) {
    const n = aNumero(v);
    return Number.isFinite(n) ? String(n) : texto(v);
  }
  return texto(v);
};

/**
 * Datos físicos a actualizar en una unidad que ya existe: solo las celdas con
 * valor que difieren de lo guardado (una celda vacía no borra nada), tipados
 * como los guarda el modal "Editar unidad" (número, booleano o texto).
 */
export function cambiosFisicosParaExistente(fila, unidad = {}) {
  const cambios = {};
  FISICOS_ACTUALIZABLES.forEach((k) => {
    const v = texto(fila[k]);
    if (v === "") return;
    let nuevo;
    if (NUMERICOS.has(k)) {
      nuevo = aNumero(v);
      if (!Number.isFinite(nuevo)) return;
    } else if (k === "balcony" || k === "garage") {
      const s = siNo(v);
      if (!s) return;
      nuevo = s === "si";
    } else {
      nuevo = orientacion(v) || v;
    }
    if (comparable(k, nuevo) !== comparable(k, unidad[k])) cambios[k] = nuevo;
  });
  return cambios;
}

/** Suma filas nuevas al borrador sin repetir unidades que ya están (gana lo que ya se editó). */
export function sumarAlBorrador(filas, nuevas) {
  const claves = new Set(filas.filter(tieneClave).map((f) => claveUnidad(f.number, f.floor)));
  return [...filas, ...nuevas.filter((f) => !(tieneClave(f) && claves.has(claveUnidad(f.number, f.floor))))];
}

// ── Persistencia (localStorage por edificio) ──
const PREFIJO = "habitia.borradorUnidades.";
export const claveBorrador = (buildingId) => `${PREFIJO}${buildingId}`;
const almacen = () => (typeof window !== "undefined" ? window.localStorage : null);

/** { filas, actualizadoEn } o null si no hay borrador (o no se puede leer). */
export function leerBorrador(buildingId, storage = almacen()) {
  if (buildingId == null || !storage) return null;
  try {
    const d = JSON.parse(storage.getItem(claveBorrador(buildingId)) || "null");
    if (!d || !Array.isArray(d.filas) || d.filas.length === 0) return null;
    const filas = d.filas.filter((f) => f && typeof f === "object").map((f) => (f._id ? f : filaDesdeUnidad(f)));
    return filas.length ? { filas, actualizadoEn: Number(d.actualizadoEn) || null } : null;
  } catch {
    return null;
  }
}

/** Guarda (o borra, si no quedan filas). Devuelve la fecha guardada o null si falló. */
export function guardarBorrador(buildingId, filas, storage = almacen()) {
  if (buildingId == null || !storage) return null;
  try {
    if (!filas || filas.length === 0) {
      storage.removeItem(claveBorrador(buildingId));
      return null;
    }
    const actualizadoEn = Date.now();
    storage.setItem(claveBorrador(buildingId), JSON.stringify({ version: 1, actualizadoEn, filas }));
    return actualizadoEn;
  } catch {
    return null;
  }
}

export function borrarBorrador(buildingId, storage = almacen()) {
  if (buildingId == null || !storage) return;
  try {
    storage.removeItem(claveBorrador(buildingId));
  } catch {
    // Sin localStorage disponible no hay nada que borrar.
  }
}
